import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Routes from 'lib/utils/routes';
import { actionSetLaborTicketSplitDraft } from 'lib/actions';
import { getLaborTicketSplitDraft } from 'lib/selectors/getLaborTicketSplitDraft';
import { getMachine } from 'lib/selectors/getMachine';
import { throwErrorToast } from 'lib/utils/toast';
import { buildSplitDestination } from '../helpers';

// Choosing a split destination, shared by the job card and the modal's single
// search match. Choosing starts nothing; the operator confirms back on the form.
// Returns true when it picked.
const usePickSplitDestination = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const splitDraft = useSelector(getLaborTicketSplitDraft);
  const machine = useSelector(getMachine);
  // The first pick leaves the page. A second tap before the unmount would
  // overwrite the draft with another job.
  const pickedRef = useRef(false);

  return useCallback(
    (workOrderOp, refs = {}) => {
      if (pickedRef.current) return false;

      const destination = buildSplitDestination(workOrderOp);
      // Dashboard opens the destination run with this part operation's
      // standards, as Start Job does. Same refusal and toast as Start Job.
      if (!destination.partOperationRef) {
        throwErrorToast(t('Unable to find part operation'));
        return false;
      }

      pickedRef.current = true;
      dispatch(
        actionSetLaborTicketSplitDraft({
          ...splitDraft,
          destination: {
            ...destination,
            workOrderOperationRef:
              destination.workOrderOperationRef ??
              refs.workOrderOperationRef ??
              null,
            workOrderId: destination.workOrderId ?? refs.workOrderId ?? null,
          },
        })
      );
      // SelectJob's unmount reopens the form, so every route out matches.
      history.push(Routes.machineIdHomePath(machine.id));
      return true;
    },
    [dispatch, history, t, splitDraft, machine]
  );
};

export default usePickSplitDestination;
